/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br."Knowledge
 * for Decisions (K4D)" and the slogan"Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from"react";
import { createPortal } from"react-dom";
import { useTranslation } from"react-i18next";

export const SelectErrorMessages: React.FC<{
  errors: string[]; name: string; submitted: boolean; toPresentErrors: boolean;
}> = ({ errors, name, submitted, toPresentErrors }) => {
  if (errors.length === 0 || !toPresentErrors) return null;
  return (
    <>
      {errors.map((error) => (
        <div key={error} id={`${name}-error`} data-testid="spanElement"
          className={`text-xs text-[var(--status-error-text)] mt-1 ${submitted ?"animate-pulse" :""}`}
          role="alert" aria-live="polite">{error}</div>
      ))}
    </>
  );
};

export const MobileOverlay: React.FC<{
  show: boolean; mobileInputRef: React.RefObject<HTMLInputElement | null>;
  errors: Set<string>; children: string; required: boolean; immediateValidation: boolean;
  name: string; handleQueryChange: (v: string) => void;
  wrappedHandleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  query: string; renderOptionsList: () => React.ReactNode; onClose: () => void;
}> = ({ show, mobileInputRef, errors, children, required, immediateValidation, name,
  handleQueryChange, wrappedHandleKeyDown, query, renderOptionsList, onClose }) => {
  const { t } = useTranslation("common");
  if (!show) return null;
  const borderClass = errors.size ?"border-[var(--status-error-border)]" :"border-[var(--border-medium)]";
  return (
    <div data-testid="mobile-select-overlay" className="fixed inset-0 z-[10050] flex flex-col justify-end"
      role="dialog" aria-modal="true">
      <div data-testid="sheet-backdrop" className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div data-testid="sheet-content"
        className="relative bg-[var(--surface-primary)] rounded-t-(--radius-lg) max-h-[70dvh] flex flex-col shadow-[0_-4px_24px_var(--shadow-lg)]">
        <div className="flex items-center justify-center pt-3 pb-1">
          <div data-testid="sheet-drag-handle" className="w-10 h-1 rounded-full bg-[var(--border-medium)]" />
        </div>
        <div className="w-full border-b border-[var(--border-subtle)] px-4 pb-3 shrink-0">
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm leading-tight text-[var(--text-primary)]" htmlFor={`${name}-mobile-display`}>
              {children}{required && <span className="text-[var(--seed-base)] ml-0.5"> *</span>}
            </label>
            <button data-testid="sheet-close-button" type="button" onClick={onClose}
              className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors p-1 -mr-1" aria-label={t("a11y.close")}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <input ref={mobileInputRef as unknown as React.LegacyRef<HTMLInputElement>}
            className={`auth-input w-full h-10 px-3 py-2 text-sm bg-[var(--surface-primary)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--seed-base)] placeholder:text-[var(--text-tertiary)] transition-colors duration-200 border ${borderClass}`}
            placeholder={children} required={required} data-immediate-validation={immediateValidation}
            name={`${name}-mobile-display`} id={`${name}-mobile-display`}
            onChange={(e) => handleQueryChange(e.target.value)} autoComplete="off"
            onKeyDown={wrappedHandleKeyDown} value={query}
            aria-describedby={errors.size > 0 ?`${name}-error` : undefined} />
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain">{renderOptionsList()}</div>
      </div>
    </div>
  );
};

export const DesktopDropdown: React.FC<{
  shouldShowMobileOverlay: boolean; isFocused: boolean; renderOptionsList: () => React.ReactNode;
  anchorRef: React.RefObject<HTMLDivElement | null>;
  interactingRef: React.MutableRefObject<boolean>;
}> = ({ shouldShowMobileOverlay, isFocused, renderOptionsList, anchorRef, interactingRef }) => {
  const [pos, setPos] = React.useState<{ top: number; left: number; width: number } | null>(null);

  React.useEffect(() => {
    if (!isFocused || shouldShowMobileOverlay || !anchorRef.current) { setPos(null); return; }
    const update = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      // 4px below the trigger: enough to read as a separate surface without
      // the two borders doubling up into a 2px line.
      if (rect) setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
  }, [isFocused, shouldShowMobileOverlay, anchorRef]);

  if (shouldShowMobileOverlay || !isFocused || !pos) return null;
  return createPortal(
    <div data-select-portal
      onMouseDown={() => { interactingRef.current = true; }}
      onMouseUp={() => { setTimeout(() => { interactingRef.current = false; }, 0); }}
      // Chrome is inherited from the trigger button (TriggerButton above):
      // same radius, same border token, same surface. The panel used to be
      // square-cornered with a heavier border and a raw Tailwind shadow, so
      // it read as a foreign element floating under the field.
      className="bg-(--surface-secondary) border border-(--border-subtle) rounded-(--radius-md) overflow-y-auto overflow-x-hidden p-0 max-h-60 shadow-[var(--shadow-md)]"
      style={{ position:'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 10050 }}>
      {renderOptionsList()}
    </div>,
    document.body,
  );
};
